import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip,
  Typography,
  Checkbox,
  IconButton,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import BuildIcon from "@mui/icons-material/Build";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import UploadIcon from "@mui/icons-material/Upload";
import AddIcon from "@mui/icons-material/Add";
import FolderIcon from "@mui/icons-material/Folder";
import Description from "@mui/icons-material/Description";
import { TestTarget, TestTargetType } from "../types/TestTarget";
import { SSTFApiClient, Workspace, Project } from "../services/api";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import {
  ApiConfig,
  saveApiConfigToStorage,
} from "../config/apiConfig";
import { countPendingReviewItems } from "../utils/statusUtils";
import { getViewPaths } from "../utils/viewPathUtils";
import { collect_views_under_root } from "../utils/testTargetHierarchy";
import { is_feature_target } from "../utils/viewItemTree";
import { MANUAL_PATHS, buildManualUrl } from "../config/manualUrl";
import { SpecificationImportDialog } from "./SpecificationImportDialog";
import type { DeletionCandidate, SpecImportSummary } from "../utils/spec-import/types";
import type { GenerateFromOperationLogResponse } from "../services/operationLogService";


/** サイドバー下部の「追加」系アウトラインボタン（サービス追加・コンポーネント追加で共通） */
const SIDEBAR_OUTLINE_ADD_BUTTON_SX = {
  fontSize: "0.75rem",
  borderColor: "#667eea",
  color: "#667eea",
  "&:hover": {
    borderColor: "#5a67d8",
    background: "rgba(102, 126, 234, 0.06)",
  },
} as const;

/** 一覧名ツールチップ: 初回・別要素への切り替えとも同じ待ち時間 */
const SIDEBAR_LIST_TOOLTIP_DELAY_MS = 500;

// Styled Components
const Container = styled.div<{ width: number }>`
  width: ${(props) => `${props.width}px`};
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
  border-right: 1px solid #e2e8f0;
  overflow: hidden;
  position: relative;
`;

const SidebarHeader = styled.div`
  padding: 20px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  position: relative;
  
  .header-content {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .header-icon {
    color: #667eea;
    display: flex;
    align-items: center;
  }
  
  h3 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 700;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const ServiceListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px;
  
  /* スクロールバーのスタイリング */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(148, 163, 184, 0.5);
    }
  }
`;

/** サービス配下のビュー・アイテム一覧の下に置く「サービス追加」行 */
const AddServiceBottomRow = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
`;

const AddServiceButton: React.FC<{
  canCreateService: boolean;
  onCreateService: () => void;
}> = ({ canCreateService, onCreateService }) => (
  <AddServiceBottomRow>
    <Tooltip
      title={
        canCreateService
          ? "新しいサービスを追加"
          : "API を有効にし、ワークスペース ID とプロジェクト ID を設定してください"
      }
      arrow
      placement="top"
    >
      <span style={{ display: "block" }}>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={onCreateService}
          disabled={!canCreateService}
          aria-label="サービスを追加"
          sx={SIDEBAR_OUTLINE_ADD_BUTTON_SX}
        >
          サービスを追加
        </Button>
      </span>
    </Tooltip>
  </AddServiceBottomRow>
);

const ServiceItem = styled.div<{ selected?: boolean }>`
  position: relative;
  cursor: default;
  border-left: 3px solid ${(props) =>
    props.selected ? "#667eea" : "transparent"};
  background: ${(props) => (props.selected ? "#f8fafc" : "transparent")};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${(props) => (props.selected ? "#f1f5f9" : "#f8fafc")};
    border-left-color: ${(props) => (props.selected ? "#667eea" : "#cbd5e1")};
  }
`;

const ServiceContent = styled.div<{ selected?: boolean; expanded?: boolean; hasExpandIcon?: boolean }>`
  padding: 8px 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-height: 48px;
  position: relative;

  .service-info {
    flex: 1;
    min-width: 0;
    padding-right: 30px; /* バッジ用のスペースを確保 */
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .service-name {
    font-weight: 600;
    font-size: 0.9rem;
    color: ${(props) => (props.selected ? "#667eea" : "#1e293b")};
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .service-meta {
    font-size: 0.75rem;
    color: #64748b;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .badge-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
    position: absolute;
    right: ${(props) => (props.hasExpandIcon ? "40px" : "16px")};
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    flex-shrink: 0;
  }
  
  .expand-icon {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
  }
  
  .feature-count {
    background: #e2e8f0;
    color: #475569;
    padding: 1px 6px;
    border-radius: 10px;
    font-weight: 500;
    font-size: 0.7rem;
    white-space: nowrap;
  }

  .expand-icon {
    font-size: 0.8rem;
    color: #94a3b8;
    transition: transform 0.2s ease;
    flex-shrink: 0;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;

    &:hover {
      background: rgba(148, 163, 184, 0.1);
    }
  }
`;

const FeatureList = styled.div`
  background: #f1f5f9;
  border-top: 1px solid #e2e8f0;
`;

const FeatureItem = styled.div<{ $isComponent?: boolean }>`
  padding: 6px 16px;
  font-size: 0.8rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #e2e8f0;
  position: relative;
  background: ${(p) =>
    p.$isComponent ? "rgba(13, 148, 136, 0.06)" : "transparent"};
  
  &:last-child {
    border-bottom: none;
  }
  
  .feature-icon {
    color: ${(p) => (p.$isComponent ? "#0d9488" : "#667eea")};
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
  
  .feature-content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-width: 0;
  }
  
  .feature-name {
    font-weight: 500;
    color: #475569;
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    
    .feature-name-text {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1;
      min-width: 0;
    }
  }
  
  .view-count {
    font-size: 0.7rem;
    color: #94a3b8;
    background: #e2e8f0;
    padding: 1px 5px;
    border-radius: 8px;
    font-weight: 500;
  }
`;

const SidebarAddChildRow = styled.div`
  padding: 8px 12px 10px;
  background: #f1f5f9;
  border-top: 1px solid #e2e8f0;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  margin: 16px;
  border: 1px solid rgba(226, 232, 240, 0.5);
`;

const EmptyIcon = styled.div`
  margin-bottom: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0.7;
`;

const EmptyText = styled.div`
  font-size: 0.95rem;
  line-height: 1.5;
  color: #64748b;
  margin-bottom: 6px;
  font-weight: 600;
`;

const EmptySubText = styled.div`
  font-size: 0.8rem;
  color: #94a3b8;
  font-weight: 500;
`;

// レビュー待ちバッジ
const ReviewBadge = styled.span`
  background: #FF9800;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.7rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
`;

// パス未設定警告バッジ（赤色）
const PathWarningBadge = styled.span`
  background: #F44336;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.7rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
`;

// Viewリスト用のパス未設定警告バッジ（小さなバッジ）
const ViewPathWarningBadge = styled.span`
  background: #F44336;
  color: white;
  padding: 1px 6px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.65rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 6px;
`;

// フッター用のスタイル
const SidebarFooter = styled.div`
  padding: 16px;
  border-top: 1px solid #e2e8f0;
  background: white;
  margin-top: auto;
`;

const ConfigButton = styled(Button)`
  &.MuiButton-root {
    width: 100%;
    background-color: #f8fafc;
    color: #64748b;
    border: 1px solid #e2e8f0;
    text-transform: none;
    font-weight: 600;
    font-size: 0.85rem;
    padding: 8px 12px;
    border-radius: 8px;
    transition: all 0.2s ease;
    
    &:hover {
      background-color: #f1f5f9;
      border-color: #cbd5e1;
      color: #475569;
    }
    
    .MuiButton-startIcon {
      margin-right: 6px;
    }
  }
`;

const FooterButtonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FooterAccountRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const FooterContextLabel = styled.span`
  flex: 1;
  min-width: 0;
  color: #64748b;
  font-size: 11px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow-wrap: anywhere;
`;

const FooterAvatarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  border-radius: 50%;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const FooterAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const FooterAvatarFallback = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #667eea;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
`;

interface ServiceListProps {
  services: TestTarget[];
  selectedServiceIds: Set<string>;
  testTargets: TestTarget[];
  onServiceSelect: (service: TestTarget) => void;
  onViewSelect?: (view: TestTarget) => void;
  onOpenServiceDetail?: (service: TestTarget) => void;
  apiConfig: ApiConfig;
  onApiConfigChange: (config: ApiConfig) => void;
  onApiClientChange: (client: SSTFApiClient | null) => void;
  workspaceId?: string;
  projectId?: string;
  onWorkspaceIdChange?: (workspaceId: string) => void;
  onProjectIdChange?: (projectId: string) => void;
  onOperationLogImport?: () => void;
  onLoadGenerationResultJson?: () => void;
  isProcessingOperationLog?: boolean;
  processingMessage?: string;
  onTransitionGraphImport?: () => void;
  onTransitionGraphCreate?: () => void;
  onSpecImportReady?: (
    response: GenerateFromOperationLogResponse,
    summary: SpecImportSummary,
    matchedExistingIds: Set<string>,
    deletionCandidates: DeletionCandidate[],
  ) => void;
  /** 外部から仕様書インポートダイアログの open 状態を制御する（省略時は内部状態で管理） */
  specImportDialogOpen?: boolean;
  onSpecImportDialogOpenChange?: (open: boolean) => void;
  showApiConfigDialog?: boolean;
  onApiConfigComplete?: () => void;
  width?: number;
  onWidthChange?: (width: number) => void;
  /** 左サイドバーからサービスを新規作成する（API・WS・PJ が揃っているとき有効） */
  onCreateService?: () => void;
  /** サービス配下にビュー／コンポーネントを追加（グラフの「ビューを追加」と同じ作成フロー） */
  onAddChild?: (parentId: string, type: TestTargetType) => void;
}

const ServiceList: React.FC<ServiceListProps> = ({
  services,
  selectedServiceIds,
  testTargets,
  onServiceSelect,
  onViewSelect,
  onOpenServiceDetail,
  onOperationLogImport,
  onLoadGenerationResultJson,
  onTransitionGraphImport,
  onTransitionGraphCreate,
  onSpecImportReady,
  specImportDialogOpen: specImportDialogOpenProp,
  onSpecImportDialogOpenChange,
  apiConfig,
  onApiConfigChange,
  onApiClientChange,
  workspaceId = "",
  projectId = "",
  onWorkspaceIdChange,
  onProjectIdChange,
  isProcessingOperationLog = false,
  processingMessage = "",
  showApiConfigDialog = false,
  onApiConfigComplete,
  width = 300,
  onWidthChange,
  onCreateService,
  onAddChild,
}) => {
  const { showSuccess, showError } = useToast();
  const { logout, user } = useAuth();
  const canCreateService = Boolean(
    apiConfig.isEnabled && workspaceId && projectId && onCreateService
  );
  const canAddChild = Boolean(
    apiConfig.isEnabled && workspaceId && projectId && onAddChild
  );

  // 階層展開状態を管理（サービスIDをキーとする）
  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());

  // 仕様書インポートダイアログの開閉状態（外部 prop が渡された場合は外部制御優先）
  const [specImportDialogOpenInternal, setSpecImportDialogOpenInternal] = useState(false);
  const specImportDialogOpen =
    specImportDialogOpenProp !== undefined ? specImportDialogOpenProp : specImportDialogOpenInternal;
  const setSpecImportDialogOpen = (open: boolean) => {
    if (specImportDialogOpenProp === undefined) {
      setSpecImportDialogOpenInternal(open);
    }
    onSpecImportDialogOpenChange?.(open);
  };

  // 選択されたサービスが変わったときの処理（自動展開はしない）
  // ユーザーが明示的に展開アイコンをクリックした場合のみ展開される

  // 階層の展開/折りたたみをトグル
  const toggleExpanded = (serviceId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // サービス選択イベントの伝播を防ぐ
    setExpandedServices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };

  // API設定ダイアログの状態
  const [showConfigDialog, setShowConfigDialog] = useState(showApiConfigDialog);
  const [localApiConfig, setLocalApiConfig] = useState<ApiConfig>(apiConfig);
  const [localWorkspaceId, setLocalWorkspaceId] = useState<string>(workspaceId);
  const [localProjectId, setLocalProjectId] = useState<string>(projectId);

  // showApiConfigDialog propが変更されたときにダイアログを開く
  useEffect(() => {
    if (showApiConfigDialog) {
      setShowConfigDialog(true);
      // 自動的にワークスペース一覧を取得
      fetchWorkspaces();
    }
  }, [showApiConfigDialog]);

  // ワークスペースとプロジェクトの状態
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [workspaceError, setWorkspaceError] = useState<string>("");
  const [projectError, setProjectError] = useState<string>("");

  // ダイアログが開かれたときに既存のワークスペースIDがある場合はプロジェクトを取得
  useEffect(() => {
    if (showConfigDialog && localWorkspaceId && workspaces.length > 0) {
      fetchProjects(localWorkspaceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConfigDialog, localWorkspaceId, workspaces.length]);

  // フッター表示用に、ログイン後はワークスペース／プロジェクト名を解決する
  useEffect(() => {
    if (user) {
      fetchWorkspaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user && workspaceId && !showConfigDialog) {
      fetchProjects(workspaceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, workspaceId, showConfigDialog]);

  // ワークスペース一覧を取得
  const fetchWorkspaces = async () => {
    setLoadingWorkspaces(true);
    setWorkspaceError("");

    try {
      // Auth0認証を使用してワークスペースを取得
      const response = await fetch('/api/workspaces', {
        method: 'GET',
        credentials: 'include', // クッキーを含める
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const workspacesList = await response.json();
      setWorkspaces(workspacesList);
    } catch (error) {
      console.error("ワークスペース取得エラー:", error);
      setWorkspaceError(
        error instanceof Error
          ? error.message
          : "ワークスペースの取得に失敗しました"
      );
    } finally {
      setLoadingWorkspaces(false);
    }
  };

  // プロジェクト一覧を取得
  const fetchProjects = async (selectedWorkspaceId: string) => {
    if (!selectedWorkspaceId) {
      setProjects([]);
      return;
    }

    setLoadingProjects(true);
    setProjectError("");

    try {
      // Auth0認証を使用してプロジェクトを取得
      const response = await fetch(`/api/workspaces/${selectedWorkspaceId}/projects`, {
        method: 'GET',
        credentials: 'include', // クッキーを含める
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const projectsList = await response.json();
      setProjects(projectsList);
    } catch (error) {
      console.error("プロジェクト取得エラー:", error);
      setProjectError(
        error instanceof Error
          ? error.message
          : "プロジェクトの取得に失敗しました"
      );
    } finally {
      setLoadingProjects(false);
    }
  };

  // ワークスペース変更時の処理
  const handleWorkspaceChange = (selectedWorkspaceId: string) => {
    setLocalWorkspaceId(selectedWorkspaceId);
    setLocalProjectId(""); // プロジェクトをリセット
    if (selectedWorkspaceId) {
      fetchProjects(selectedWorkspaceId);
    } else {
      setProjects([]);
    }
  };

  // API設定ダイアログを開く
  const handleOpenConfigDialog = () => {
    setLocalApiConfig({ ...apiConfig });
    setLocalWorkspaceId(workspaceId);
    setLocalProjectId(projectId);
    setShowConfigDialog(true);

    // 自動的にワークスペース一覧を取得
    fetchWorkspaces();
  };

  // API設定の変更処理
  const handleApiConfigChange = (
    field: keyof ApiConfig,
    value: string | boolean
  ) => {
    setLocalApiConfig((prev) => ({
      ...prev,
      [field]: value,
    }));

    // URLが変更された場合は、ワークスペースとプロジェクトをクリア
    if (
      field === "sstfBaseUrl" ||
      field === "graphqlEndpoint"
    ) {
      setWorkspaces([]);
      setProjects([]);
      setLocalWorkspaceId("");
      setLocalProjectId("");
    }
  };

  // API設定を保存
  const handleSaveConfig = () => {
    // 設定をlocalStorageに保存
    saveApiConfigToStorage(localApiConfig);

    // 親コンポーネントに変更を通知
    onApiConfigChange(localApiConfig);

    // ワークスペースIDとプロジェクトIDも通知
    onWorkspaceIdChange?.(localWorkspaceId);
    onProjectIdChange?.(localProjectId);
    const workspaceChanged = localWorkspaceId !== workspaceId;
    const projectChanged = localProjectId !== projectId;
    const apiChanged =
      localApiConfig.sstfBaseUrl !== apiConfig.sstfBaseUrl ||
      localApiConfig.webBaseUrl !== apiConfig.webBaseUrl ||
      localApiConfig.graphqlEndpoint !== apiConfig.graphqlEndpoint;
    
    // NOTE: 設定値が変更された場合、workspaceIdとprojectIdが設定されているか検証してからナビゲート
    if (workspaceChanged || projectChanged || apiChanged) {
      if (apiChanged && (!localWorkspaceId || !localProjectId)) {
        showError("API設定を変更する場合は、ワークスペースとプロジェクトを選択してください。");
        return;
      }
      
      sessionStorage.setItem("testTargetManager_suppressAutoConfigDialog", "true");
      localStorage.removeItem("testTargetManager_workspaceId");
      localStorage.removeItem("testTargetManager_projectId");
      if (localWorkspaceId) {
        localStorage.setItem("testTargetManager_workspaceId", localWorkspaceId);
      }
      if (localProjectId) {
        localStorage.setItem("testTargetManager_projectId", localProjectId);
      }
      const nextPath =
        localWorkspaceId && localProjectId
          ? `/${localWorkspaceId}/${localProjectId}`
          : localWorkspaceId
          ? `/${localWorkspaceId}`
          : "/";
      window.location.assign(nextPath);
      return;
    }

    // ワークスペースとプロジェクトが選択されているかチェック
    if (!localWorkspaceId || !localProjectId) {
      showError("ワークスペースとプロジェクトを選択してください。");
      return;
    }

    // 設定を保存
    setShowConfigDialog(false);
    onApiConfigComplete?.(); // Gọi callback để đóng dialog
    showSuccess("設定を保存しました。");
  };

  const clear_stored_cookies = () => {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.toLowerCase().includes("cookie")) {
        localStorage.removeItem(key);
      }
    });
  };

  const handle_logout = () => {
    clear_stored_cookies();
    logout();
  };

  const selectedWorkspaceName = workspaces.find(
    (workspace) => workspace.workspace_id === workspaceId
  )?.name;
  const selectedProjectName = projects.find(
    (project) => project.project_id === projectId
  )?.name;

  /** Service 直下から再帰的に取得する全子孫（深さ優先・作成順に近い順） */
  const getAllDescendants = (parentId: string): TestTarget[] => {
    const directChildren = testTargets.filter((t) => t.parentId === parentId);
    let allDescendants: TestTarget[] = [];

    directChildren.forEach((child) => {
      allDescendants.push(child);
      allDescendants.push(...getAllDescendants(child.id));
    });

    return allDescendants;
  };

  const getAllViewsUnderService = (serviceId: string) => {
    return collect_views_under_root(testTargets, serviceId);
  };

  const getAllComponentsUnderService = (serviceId: string) => {
    return getAllDescendants(serviceId).filter((t) => t.type === "component");
  };

  /** サイドバー表示用: component をすべての view より上に並べた一覧 */
  const getServiceViewsAndComponentsForSidebar = (serviceId: string) => {
    const all = getAllDescendants(serviceId);
    const components = all.filter((t) => t.type === "component");
    const views = all.filter((t) => t.type === "view");
    return [...components, ...views];
  };

  /**
   * サイドバー表示用の階層ツリー。
   * category はフォルダーとして表示し、その配下の view / component / 子category を字下げして並べる。
   * category 直下に無い（Service 直下の）view / component は depth 0 で表示する。
   * section / overlay / item はサイドバーには出さない（従来どおり item 数は getItemCount で集計）。
   */
  const getServiceSidebarTree = (
    serviceId: string
  ): { target: TestTarget; depth: number }[] => {
    const out: { target: TestTarget; depth: number }[] = [];
    const seen = new Set<string>();
    const walk = (parentId: string, depth: number) => {
      const children = testTargets
        .filter((t) => t.parentId === parentId)
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
      for (const child of children) {
        if (seen.has(child.id)) continue;
        if (child.type === "category") {
          seen.add(child.id);
          out.push({ target: child, depth });
          walk(child.id, depth + 1);
        } else if (child.type === "view" || child.type === "component") {
          seen.add(child.id);
          out.push({ target: child, depth });
          // view / component はサイドバー上では葉として扱う（配下へは降りない）
        }
        // section / overlay / item 等はサイドバー非表示
      }
    };
    walk(serviceId, 0);
    return out;
  };

  // 選択されたサービスのView一覧を取得（後方互換のため残す）
  const getServiceViews = (serviceId: string) => {
    return getAllViewsUnderService(serviceId);
  };

  // View / component 配下の全階層の Item 数を取得
  const getItemCount = (rootId: string) => {
    const allDescendants = getAllDescendants(rootId);
    return allDescendants.filter(
      (t) => t.type === "item" && !is_feature_target(t)
    ).length;
  };

  // Service配下のパス未設定View数をカウント
  const getPathWarningCount = (serviceId: string) => {
    const views = getAllViewsUnderService(serviceId);
    return views.filter((view) => {
      const paths = getViewPaths(view.params);
      return !(paths.length > 0 && paths.some((p) => p.path && p.path.trim() !== ""));
    }).length;
  };

  // Viewがパス未設定かどうかを判定
  const hasPathWarning = (view: TestTarget) => {
    const paths = getViewPaths(view.params);
    return !(paths.length > 0 && paths.some((p) => p.path && p.path.trim() !== ""));
  };

  return (
    <Container width={width}>
      <SidebarHeader>
        <div className="header-content">
          <div className="header-icon">
            <BusinessIcon sx={{ fontSize: 24 }} />
          </div>
          <h3>サービス一覧</h3>
          {onLoadGenerationResultJson && (
            <Tooltip
              title={
                !apiConfig.isEnabled
                  ? "API設定を有効にしてください"
                  : !workspaceId || !projectId
                  ? "ワークスペースIDとプロジェクトIDを設定してください"
                  : "保存した生成結果JSONを読み込み、登録ダイアログを表示します"
              }
              arrow
              placement="bottom"
            >
              <IconButton
                size="small"
                onClick={onLoadGenerationResultJson}
                disabled={
                  !apiConfig.isEnabled || !workspaceId || !projectId
                }
                sx={{ ml: 0.5, p: 0.25 }}
                aria-label="生成結果JSONを読み込む"
              >
                <UploadIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </div>
        {onSpecImportReady && (
          <div style={{ marginTop: "12px" }}>
            <Tooltip
              title={
                !apiConfig.isEnabled
                  ? "API設定を有効にしてください"
                  : !workspaceId || !projectId
                  ? "ワークスペースIDとプロジェクトIDを設定してください"
                  : "仕様書ファイルをインポートしてテストターゲットを生成します"
              }
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Description />}
                  onClick={() => setSpecImportDialogOpen(true)}
                  disabled={
                    !apiConfig.isEnabled ||
                    !workspaceId ||
                    !projectId
                  }
                  sx={{ fontSize: "0.75rem", width: "100%" }}
                >
                  仕様書のインポート
                </Button>
              </span>
            </Tooltip>
          </div>
        )}
        {onOperationLogImport && (
          <div style={{ marginTop: "12px" }}>
            <Tooltip
              title={
                isProcessingOperationLog
                  ? processingMessage || "操作ログを処理中です..."
                  : !apiConfig.isEnabled
                  ? "API設定を有効にしてください"
                  : !workspaceId || !projectId
                  ? "ワークスペースIDとプロジェクトIDを設定してください"
                  : "操作ログファイルをアップロードしてテストターゲットを生成します"
              }
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    isProcessingOperationLog ? (
                      <CircularProgress size={16} />
                    ) : (
                      <CloudUploadIcon />
                    )
                  }
                  onClick={onOperationLogImport}
                  disabled={
                    !apiConfig.isEnabled ||
                    !workspaceId ||
                    !projectId ||
                    isProcessingOperationLog
                  }
                  sx={{ fontSize: "0.75rem", width: "100%" }}
                >
                  {isProcessingOperationLog
                    ? "処理中..."
                    : "操作ログのインポート"}
                </Button>
              </span>
            </Tooltip>
          </div>
        )}
        {onTransitionGraphImport && (
          <div style={{ marginTop: "12px" }}>
            <Tooltip
              title={
                !apiConfig.isEnabled
                  ? "API設定を有効にしてください"
                  : !workspaceId || !projectId
                  ? "ワークスペースIDとプロジェクトIDを設定してください"
                  : "Mermaid形式のグラフテキストから遷移情報を一括登録します"
              }
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AccountTreeIcon />}
                  onClick={onTransitionGraphImport}
                  disabled={
                    !apiConfig.isEnabled ||
                    !workspaceId ||
                    !projectId
                  }
                  sx={{ fontSize: "0.75rem", width: "100%" }}
                >
                  遷移情報の一括登録
                </Button>
              </span>
            </Tooltip>
          </div>
        )}
        {onTransitionGraphCreate && (
          <div style={{ marginTop: "12px" }}>
            <Tooltip
              title={
                !apiConfig.isEnabled
                  ? "API設定を有効にしてください"
                  : !workspaceId || !projectId
                  ? "ワークスペースIDとプロジェクトIDを設定してください"
                  : "操作ログから遷移情報を作成します"
              }
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AccountTreeIcon />}
                  onClick={onTransitionGraphCreate}
                  disabled={
                    !apiConfig.isEnabled ||
                    !workspaceId ||
                    !projectId
                  }
                  sx={{ fontSize: "0.75rem", width: "100%" }}
                >
                  遷移情報のGraphデータ生成
                </Button>
              </span>
            </Tooltip>
          </div>
        )}
      </SidebarHeader>
      <ServiceListContainer>
        {services.length === 0 ? (
          <>
            <EmptyState>
              <EmptyIcon>
                <BusinessIcon sx={{ fontSize: 40, color: "#94a3b8" }} />
              </EmptyIcon>
              <EmptyText>サービスが見つかりません</EmptyText>
              <EmptySubText>サービスがありません</EmptySubText>
            </EmptyState>
            {onCreateService ? (
              <AddServiceButton
                canCreateService={canCreateService}
                onCreateService={onCreateService}
              />
            ) : null}
          </>
        ) : (
          <>
            {services.map((service) => {
              const views = getServiceViews(service.id);
              const components = getAllComponentsUnderService(service.id);
              const sidebarTree = getServiceSidebarTree(service.id);
              const isSelected = selectedServiceIds.has(service.id);
              const isExpanded = expandedServices.has(service.id);
              const reviewCount = countPendingReviewItems(service.id, testTargets);
              const pathWarningCount = getPathWarningCount(service.id);
              const totalItems = getItemCount(service.id);
              const sidebarCountsCompact = `${components.length}C · ${views.length}V · ${totalItems}I`;
              const sidebarCountsTooltip = `${components.length}コンポーネント・${views.length}ビュー・${totalItems}アイテム`;
              const showServiceChildPanel =
                sidebarTree.length > 0 || canAddChild;

              return (
                <ServiceItem
                  key={service.id}
                  selected={isSelected}
                >
                  <ServiceContent
                    selected={isSelected}
                    expanded={isExpanded && showServiceChildPanel}
                    hasExpandIcon={showServiceChildPanel}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        onServiceSelect(service);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{
                        padding: "4px",
                        color: "#667eea",
                        "&.Mui-checked": {
                          color: "#667eea",
                        },
                      }}
                    />
                    <div className="service-info">
                      <Tooltip
                        title={onOpenServiceDetail ? "クリックで詳細を開く" : service.name}
                        arrow
                        placement="top"
                        enterDelay={SIDEBAR_LIST_TOOLTIP_DELAY_MS}
                        enterNextDelay={SIDEBAR_LIST_TOOLTIP_DELAY_MS}
                      >
                        <div
                          className="service-name"
                          onClick={(e) => {
                            if (onOpenServiceDetail) {
                              e.stopPropagation();
                              onOpenServiceDetail(service);
                            }
                          }}
                          style={{
                            cursor: onOpenServiceDetail ? "pointer" : undefined,
                          }}
                        >
                          {service.name}
                        </div>
                      </Tooltip>
                      <div className="service-meta">
                        <Tooltip
                          title={sidebarCountsTooltip}
                          arrow
                          placement="top"
                          enterDelay={SIDEBAR_LIST_TOOLTIP_DELAY_MS}
                          enterNextDelay={SIDEBAR_LIST_TOOLTIP_DELAY_MS}
                        >
                          <span className="feature-count">
                            {sidebarCountsCompact}
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                    {/* バッジコンテナ（縦並び） */}
                    {(pathWarningCount > 0 || reviewCount > 0) && (
                      <div className="badge-container">
                        {pathWarningCount > 0 && (
                          <PathWarningBadge>
                            {pathWarningCount}
                          </PathWarningBadge>
                        )}
                        {reviewCount > 0 && (
                          <ReviewBadge>{reviewCount}</ReviewBadge>
                        )}
                      </div>
                    )}
                    {showServiceChildPanel && (
                      <div
                        className="expand-icon"
                        onClick={(e) => toggleExpanded(service.id, e)}
                        style={{
                          transform: isExpanded ? "translateY(-50%) rotate(90deg)" : "translateY(-50%) rotate(0deg)",
                        }}
                      >
                        ▶
                      </div>
                    )}
                  </ServiceContent>

                {isExpanded && showServiceChildPanel && (
                  <>
                    {sidebarTree.length > 0 ? (
                      <FeatureList>
                        {sidebarTree.map(({ target, depth }) => {
                          const isComponent = target.type === "component";
                          const isCategory = target.type === "category";
                          return (
                            <FeatureItem
                              key={target.id}
                              $isComponent={isComponent}
                              onClick={() => onViewSelect?.(target)}
                              style={{
                                cursor: "pointer",
                                paddingLeft: 16 + depth * 16,
                              }}
                            >
                              <div className="feature-icon">
                                {isCategory ? (
                                  <FolderIcon sx={{ fontSize: 15 }} />
                                ) : isComponent ? (
                                  <ViewModuleIcon sx={{ fontSize: 15 }} />
                                ) : (
                                  <BuildIcon sx={{ fontSize: 14 }} />
                                )}
                              </div>
                              <div className="feature-content">
                                <Tooltip
                                  title={target.name}
                                  arrow
                                  placement="top"
                                  enterDelay={SIDEBAR_LIST_TOOLTIP_DELAY_MS}
                                  enterNextDelay={SIDEBAR_LIST_TOOLTIP_DELAY_MS}
                                >
                                  <div className="feature-name">
                                    <span className="feature-name-text">
                                      {target.name}
                                    </span>
                                    {!isComponent &&
                                      !isCategory &&
                                      hasPathWarning(target) && (
                                        <ViewPathWarningBadge>!</ViewPathWarningBadge>
                                      )}
                                  </div>
                                </Tooltip>
                                <div className="view-count">
                                  {getItemCount(target.id)}
                                </div>
                              </div>
                            </FeatureItem>
                          );
                        })}
                      </FeatureList>
                    ) : null}
                    <SidebarAddChildRow>
                      <Tooltip
                        title={
                          canAddChild
                            ? "このサービス配下に新規テストターゲット（共通コンポーネント）を作成"
                            : "API を有効にし、ワークスペース ID とプロジェクト ID を設定してください"
                        }
                        arrow
                        placement="top"
                      >
                        <span style={{ display: "block" }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            disabled={!canAddChild}
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddChild?.(service.id, "component");
                            }}
                            aria-label="新規テストターゲット作成"
                            sx={SIDEBAR_OUTLINE_ADD_BUTTON_SX}
                          >
                            新規テストターゲット作成
                          </Button>
                        </span>
                      </Tooltip>
                    </SidebarAddChildRow>
                  </>
                )}
                </ServiceItem>
              );
            })}
            {onCreateService ? (
              <AddServiceButton
                canCreateService={canCreateService}
                onCreateService={onCreateService}
              />
            ) : null}
          </>
        )}
      </ServiceListContainer>

      {/* フッター - API設定ボタン */}
      <SidebarFooter>
        <FooterButtonStack>
          {user && (
            <FooterAccountRow
              title={
                selectedWorkspaceName && selectedProjectName
                  ? `${selectedWorkspaceName} / ${selectedProjectName}`
                  : undefined
              }
            >
              <FooterContextLabel>
                {selectedWorkspaceName && selectedProjectName
                  ? `${selectedWorkspaceName} / ${selectedProjectName}`
                  : "未設定"}
              </FooterContextLabel>
              <FooterAvatarButton
                type="button"
                aria-label="ユーザーメニュー"
                onClick={handleOpenConfigDialog}
              >
                {user.iconUrl ? (
                  <FooterAvatar
                    src={user.iconUrl}
                    alt={user.name || user.email || ""}
                  />
                ) : (
                  <FooterAvatarFallback aria-hidden="true">
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </FooterAvatarFallback>
                )}
              </FooterAvatarButton>
            </FooterAccountRow>
          )}
          <ConfigButton
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={handleOpenConfigDialog}
          >
            API設定
          </ConfigButton>
          <ConfigButton
            variant="outlined"
            startIcon={<MenuBookIcon />}
            onClick={() => {
              window.open(
                buildManualUrl(MANUAL_PATHS.targetManagerMain),
                "_blank",
              );
            }}
          >
            利用マニュアル
          </ConfigButton>
          <ConfigButton
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={handle_logout}
          >
            ログアウト
          </ConfigButton>
        </FooterButtonStack>
      </SidebarFooter>

      {/* API設定ダイアログ */}
      <Dialog
        open={showConfigDialog}
        onClose={() => {
          setShowConfigDialog(false);
          onApiConfigComplete?.();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>ワークスペース・プロジェクト選択</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              marginBottom: 2,
              color: "#666",
              textAlign: "left",
            }}
          >
            ワークスペースとプロジェクトを選択してください。
          </Typography>
          {/* ワークスペース選択 */}
          <FormControl fullWidth margin="normal">
            <InputLabel>ワークスペース</InputLabel>
            <Select
              value={localWorkspaceId}
              onChange={(e) => handleWorkspaceChange(e.target.value)}
              label="ワークスペース"
              disabled={loadingWorkspaces}
              endAdornment={loadingWorkspaces && <CircularProgress size={20} />}
            >
              <MenuItem value="">ワークスペースを選択</MenuItem>
              {workspaces.map((workspace) => (
                <MenuItem
                  key={workspace.workspace_id}
                  value={workspace.workspace_id}
                >
                  {workspace.name}
                </MenuItem>
              ))}
            </Select>
            {workspaceError && (
              <div
                style={{
                  color: "#d32f2f",
                  fontSize: "0.75rem",
                  marginTop: "4px",
                }}
              >
                {workspaceError}
              </div>
            )}
          </FormControl>

          {/* プロジェクト選択 */}
          <FormControl fullWidth margin="normal">
            <InputLabel>プロジェクト</InputLabel>
            <Select
              value={localProjectId}
              onChange={(e) => setLocalProjectId(e.target.value)}
              label="プロジェクト"
              disabled={loadingProjects || !localWorkspaceId}
              endAdornment={loadingProjects && <CircularProgress size={20} />}
            >
              <MenuItem value="">プロジェクトを選択</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.project_id} value={project.project_id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
            {projectError && (
              <div
                style={{
                  color: "#d32f2f",
                  fontSize: "0.75rem",
                  marginTop: "4px",
                }}
              >
                {projectError}
              </div>
            )}
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={localApiConfig.isEnabled}
                onChange={(e) =>
                  handleApiConfigChange("isEnabled", e.target.checked)
                }
              />
            }
            label="API機能を有効化"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfigDialog(false)}>キャンセル</Button>
          <Button onClick={handleSaveConfig} variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {onSpecImportReady && (
        <SpecificationImportDialog
          open={specImportDialogOpen}
          onClose={() => setSpecImportDialogOpen(false)}
          existingTargets={testTargets}
          onPreviewReady={onSpecImportReady}
        />
      )}
    </Container>
  );
};

export default ServiceList;
